'use client';

import type {FC} from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import {usePathname} from 'next/navigation';

import {DOCUMENT_TYPE_ABOUT_FRONTPAGE} from '@/modules/finkn/about/frontpage/sanity/constants';
import {
	NavigationMenu,
	NavigationMenuArticleLink,
	NavigationMenuContent,
	NavigationMenuContentGroup,
	NavigationMenuContentGroupList,
	NavigationMenuContentGroupListItem,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	NavigationMenuViewport,
} from '@/modules/finkn/components/navigation-menu';
import {DOCUMENT_TYPE_SEARCH_FRONTPAGE} from '@/modules/finkn/search/sanity/constants';
import {SearchIcon} from '@/modules/foundation/components/icons/search-icon';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';
import {ABOUT_PAGE_PATH, SEARCH_PAGE_PATH} from '@/shared/constants';

import {LoginButton} from './LoginButton';
import type {Navigation, NavItem} from './Navbar';

type Props = {
	navigation: Navigation;
};

export const DesktopNav: FC<Props> = ({navigation}) => {
	const pathname = usePathname();

	const {insurance, bank, collection, loginFrontpageTitle} = navigation;

	const basePath = pathname.split('/')[1];

	return (
		<NavigationMenu
			className={clsx(
				'z-50',
				'hidden',
				'min-[1070px]:flex',
				'justify-between',
				'items-end',
				'-mt-1',
			)}
		>
			<NavigationMenuList aria-label="Fagområder">
				<NavItemComponent
					navItem={insurance}
					activePath={basePath === insurance.href.replace('/', '')}
				/>
				<NavItemComponent
					navItem={bank}
					activePath={basePath === bank.href.replace('/', '')}
				/>
				<NavItemComponent
					navItem={collection}
					activePath={basePath === collection.href.replace('/', '')}
				/>
			</NavigationMenuList>
			<div
				className={clsx(
					'flex',
					'gap-10',
					'font-display',
					'text-sm',
					'items-center',
					'relative',
					'top-[10px]',
				)}
			>
				<Link
					href={internalLinkToHref(DOCUMENT_TYPE_ABOUT_FRONTPAGE)}
					className={clsx(
						'hover:text-primary-15',
						'transition-colors',
						'relative',
						'inline-block',
						ABOUT_PAGE_PATH.replace('/', '') === basePath && [
							'after:absolute',
							'after:h-[2px]',
							'after:bg-primary-2',
							'after:w-full',
							'after:-bottom-1',
							'after:left-0',
						],
					)}
				>
					Om oss
				</Link>

				<Link
					href={internalLinkToHref(DOCUMENT_TYPE_SEARCH_FRONTPAGE)}
					className={clsx(
						'flex',
						'items-center',
						'gap-1',
						'hover:text-primary-15',
						'transition-colors',
						'relative',
						SEARCH_PAGE_PATH.replace('/', '') === basePath && [
							'after:absolute',
							'after:h-[2px]',
							'after:bg-primary-2',
							'after:w-full',
							'after:-bottom-1',
						],
					)}
				>
					<SearchIcon aria-hidden className={clsx('text-sm')} />
					Søk
				</Link>

				<LoginButton title={loginFrontpageTitle} />
			</div>
			<NavigationMenuViewport />
		</NavigationMenu>
	);
};

const NavItemComponent = ({
	navItem,
	activePath,
}: {
	navItem: NavItem;
	activePath: boolean;
}) => {
	const {areas, resources, articles} = navItem;

	return (
		<NavigationMenuItem className="mt-3">
			<NavigationMenuTrigger activePath={activePath}>
				{navItem.title}
			</NavigationMenuTrigger>
			<NavigationMenuContent>
				<NavigationMenuContentGroup>
					<NavigationMenuContentGroupList title="Temaer">
						<NavigationMenuContentGroupListItem key={navItem.title}>
							<NavigationMenuLink href={navItem.href} featured>
								Alt om {navItem.title.toLowerCase()}
							</NavigationMenuLink>
						</NavigationMenuContentGroupListItem>
						{areas.links.map((link) => (
							<NavigationMenuContentGroupListItem key={link._key}>
								<NavigationMenuLink href={link.href}>
									{link.title}
								</NavigationMenuLink>
							</NavigationMenuContentGroupListItem>
						))}
					</NavigationMenuContentGroupList>
				</NavigationMenuContentGroup>

				<NavigationMenuContentGroup>
					<NavigationMenuContentGroupList title="Ressurser">
						{resources.links.map((link, idx) => (
							<NavigationMenuContentGroupListItem key={link._key}>
								<NavigationMenuLink
									href={link.href}
									featured={idx === 0}
								>
									{link.title}
								</NavigationMenuLink>
							</NavigationMenuContentGroupListItem>
						))}
					</NavigationMenuContentGroupList>
				</NavigationMenuContentGroup>

				<NavigationMenuContentGroup>
					<NavigationMenuContentGroupList title="Aktuell nemnd">
						{navItem.nemnd.links.map((link) => (
							<NavigationMenuContentGroupListItem key={link._key}>
								<NavigationMenuLink href={link.href}>
									{link.title}
								</NavigationMenuLink>
							</NavigationMenuContentGroupListItem>
						))}
					</NavigationMenuContentGroupList>
				</NavigationMenuContentGroup>

				{articles?.length ? (
					<NavigationMenuContentGroup>
						<NavigationMenuContentGroupList
							title="Nyheter"
							hideTitle
							className={clsx('grid', 'gap-4')}
						>
							{articles.map((article, idx) => (
								<NavigationMenuContentGroupListItem
									key={article._key}
								>
									<NavigationMenuArticleLink
										article={article}
										idx={idx}
									/>
								</NavigationMenuContentGroupListItem>
							))}
						</NavigationMenuContentGroupList>
					</NavigationMenuContentGroup>
				) : null}
			</NavigationMenuContent>
		</NavigationMenuItem>
	);
};
